/**
 * Right-side drawer for a board ROW's comment thread.
 *
 * Board comments attach to a row via `(trackerId, rowKey)`. On a row that IS a
 * record carrying a thread of its own, an asset or a brief, this is that thread:
 * the same comments the asset viewer and the brief page show. Renders as a
 * floating card over the canvas (does NOT resize the table); the thread itself
 * lives in `ThreadedCommentsBody`.
 *
 * On mount we stamp `lastSeen` for this row so the badge flips from blue to
 * gray as soon as the user opens the panel. When opened via a deeplink
 * (`?comment=<id>`), that comment is scrolled into view and briefly highlighted.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import type { TrackerBoardType } from '../../lib/trackers-analytics';
import { useMarkRowSeenOnMount } from '../../lib/useTrackerCommentsLastSeen';
import { ThreadedCommentsBody } from './ThreadedCommentsBody';
import { useTranslation } from 'react-i18next';

interface TrackerCommentsPanelProps {
  open: boolean;
  trackerId: string;
  boardType: TrackerBoardType;
  rowKey: string | null;
  /** Deeplink target — scroll to + highlight this comment when the panel opens. */
  focusCommentId?: string | null;
  onClose: () => void;
}

/** Match the longest Tailwind duration token applied to the transform below. */
const SLIDE_OUT_MS = 300;

export function TrackerCommentsPanel(props: TrackerCommentsPanelProps) {
  const { t } = useTranslation('trackers');

  const { open, trackerId, boardType, rowKey, focusCommentId, onClose } = props;

  // Which threaded card is selected. Owned here rather than in the body so a
  // single Escape handler can close the open reply composer before the panel.
  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(null);
  useEffect(() => setSelectedCommentId(null), [open, rowKey]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (selectedCommentId !== null) setSelectedCommentId(null);
      else onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose, selectedCommentId]);

  // Mark this row as seen the moment the panel opens.
  useMarkRowSeenOnMount(open ? trackerId : null, open ? rowKey : null);

  // Snapshot of the currently-shown row. Kept alive after `open` flips to
  // false so the body remains rendered while the slide-out transition plays.
  // Cleared after `SLIDE_OUT_MS` so off-screen queries stop running and the
  // next open starts from a clean tree.
  const [shown, setShown] = useState<{
    rowKey: string;
    focusCommentId: string | null;
  } | null>(() => (open && rowKey ? { rowKey, focusCommentId: focusCommentId ?? null } : null));
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (open && rowKey) {
      // Update snapshot whenever we're opening (possibly with a new row).
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      setShown({ rowKey, focusCommentId: focusCommentId ?? null });
      return;
    }
    // Closing: keep the snapshot alive long enough for the slide-out to play.
    if (shown) {
      closeTimerRef.current = setTimeout(() => {
        setShown(null);
        closeTimerRef.current = null;
      }, SLIDE_OUT_MS);
    }
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
    // `shown` intentionally excluded — we only schedule the clear from
    // `open` flipping to false, not from snapshot changes within an open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rowKey, focusCommentId]);

  const isVisible = open && rowKey !== null;

  // Portal to <body>: the board renders inside animated/overflow-clipped
  // containers (StaggerItem's transform establishes a containing block for
  // fixed elements), which would otherwise mis-position or clip this drawer.
  if (typeof document === 'undefined') return null;

  // The panel floats as a card inset from the viewport edges, so it clears its
  // own width plus that inset when it slides away.
  const frame =
    'bottom-2 right-2 top-2 w-100 rounded-xl border border-border shadow-lg duration-base ' +
    (isVisible ? 'translate-x-0' : 'pointer-events-none translate-x-[calc(100%+0.5rem)]');

  return createPortal(
    <aside
      className={`fixed z-50 flex flex-col overflow-hidden bg-card transition-transform ease-out ${frame}`}
      data-testid="tracker-comments-panel"
      role="dialog"
      aria-label={t('trackersBoard.trackerCommentsPanel.rowComments')}
      aria-hidden={!isVisible}
    >
      {shown ? (
        <ThreadedCommentsBody
          trackerId={trackerId}
          boardType={boardType}
          rowKey={shown.rowKey}
          focusCommentId={shown.focusCommentId}
          selectedCommentId={selectedCommentId}
          onSelectComment={setSelectedCommentId}
          onClose={onClose}
        />
      ) : null}
    </aside>,
    document.body,
  );
}
